import { useState, useEffect } from 'react';
import { Divider, Collapse, Tooltip } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import dynamic from 'next/dynamic';

import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { isBlank } from 'utils/StringUtils';
import { useLocale } from 'hooks';
import { useProductSheet } from 'context/product-sheet';
import { getDomainValue } from 'utils/domain';

import styles from './style.module.css';

const PreSaleModal = dynamic(() => import('components/pdp/pre-sale-modal').then((mod) => mod.PreSaleModal));
const GuarantyInfo = dynamic(() => import('components/guarantyInfo'));
const ProductShare = dynamic(() => import('components/productShare').then((mod) => mod.ProductShare));

const ChevronSVG = dynamic(() => import('components/iconsSVG/chevron').then((mod) => mod.ChevronSVG));

const { Panel } = Collapse;

const measureImgSrc = getDomainValue({
  camper: 'https://www.camper.com/html/CAMPERLAB/GAFAS_SOL',
  nnormal: 'https://www.nnormal.com/html/CAMPERLAB/GAFAS_SOL',
  camperlab: 'https://www.camperlab.com/html/CAMPERLAB/GAFAS_SOL',
});

export function ProductInfoSection() {
  const { t, giftCardList } = useI18n();
  const { country: market } = useLocale();
  const { productSheet } = useProductSheet();
  const [openTooltipIndex, setOpenTooltipIndex] = useState(null);
  const [openDescriptionModal, setOpenDescriptionModal] = useState(false);

  const isGiftCard = giftCardList.includes(productSheet.code);

  useEffect(() => {
    window.handleKlarnaOpen = () => {
      dataLayerHandleEvent({
        event: 'item_info',
        event_category: 'product detail',
        event_detail_1: 'installment payment',
        event_detail_2: '',
      });
    };
  }, []);

  const handlerDataOpenAccordion = (key) => {
    const keyMapper = {
      1: 'description',
      2: 'features',
      3: 'product care',
    };

    if (key) {
      dataLayerHandleEvent({
        event: 'item_info',
        event_category: 'product detail',
        event_detail_1: keyMapper[key],
        event_detail_2: '',
      });
    }
  };

  const { camperlab } = productSheet;

  const showDivider =
    (market === 'AU' && camperlab === true) ||
    (getDomainValue({
      camper: true,
      camperlab: true,
      nnormal: false,
    }) &&
      productSheet.presales &&
      productSheet.presalesDate) ||
    (productSheet.guarantyInfoArray && productSheet.guarantyInfoArray.filter(Boolean).length) ||
    (productSheet.presales && productSheet.presalesDate);

  return (
    <div
      className={`${styles.productInfoSection} ${isGiftCard ? styles.infoSectionGiftCard : ''} ${!showDivider ? styles.productInfoSectionOnlyCollapse : ''}`}
      id={'product-info'}
    >
      {!isGiftCard ?
        <>
          {showDivider ?
            <Divider className={styles.customDivider} />
          : false}
          {market === 'AU' && camperlab === true && (
            <span className={styles.onlyOnCamper}>
              {t(
                'listado.productos',
                getDomainValue({
                  camper: 'only.on.camper.com',
                  camperlab: 'only.on.camper.com',
                  nnormal: 'only.on.nnormal.com',
                }),
                '***Only on camper.com',
              )}
            </span>
          )}
          {productSheet.presales && productSheet.presalesDate && (
            <div>
              <p className={styles.presaleDateText}>
                {t('ficha.producto', 'presales.estimated.date', '***Fecha estimada de entrega:')} {productSheet.presalesDate}
              </p>
              <PreSaleModal onClose={() => {}} />
            </div>
          )}
          {productSheet.guarantyInfoArray && productSheet.guarantyInfoArray.filter(Boolean).length > 0 && (
            <ul className={styles.guarantyList}>
              {productSheet?.relevantInfo && (
                <>
                  {productSheet.relevantInfo.map((item, index) => (
                    <li key={`guaranty-${index}`} className={styles.deliveryText} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </>
              )}
              {productSheet.isRecrafted && !productSheet.isRoku && (
                <li className={styles.deliveryText}>{t('listado.productos', 'text.info.recrafted', 'Your order will be delivered in 4 - 6 weeks.')}</li>
              )}
              {productSheet.isGoretex3d && <li className={styles.deliveryText}>{t('listado.productos', 'text.info.goretex3d')}</li>}
              {productSheet.isObraDeArte && <li className={styles.deliveryText}>{t('listado.productos', 'text.info.obras.de.arte')}</li>}
              {productSheet.isObraDeArteSonFortesa && <li className={styles.deliveryText}>{t('listado.productos', 'text.info.obras.de.arte.son.fortesa')}</li>}
              {productSheet.recycled ?
                <li
                  className={styles.recycledText}
                  data-info={`text.info.recycled${productSheet.recycledStatus === 'good' ? '.good' : ''}`}
                  dangerouslySetInnerHTML={{
                    __html: t('listado.productos', `text.info.recycled${productSheet.recycledStatus === 'good' ? '.good' : ''}`, '<b>Recycled</b>'),
                  }}
                ></li>
              : productSheet.guarantyInfoArray.map((info, index) => <GuarantyInfo key={`guarantee-text-${index}`} infoText={info} />)}
            </ul>
          )}
        </>
      : <>
          <Divider className={styles.customDivider} />
          <div className={styles.giftCardText} dangerouslySetInnerHTML={{ __html: t('ficha.producto', 'gift.card.pdp.text') }}></div>
        </>
      }
      {/* @ts-ignore Antd!! */}
      <Collapse
        bordered={false}
        accordion
        defaultActiveKey="1"
        expandIcon={({ isActive }) => {
          return getDomainValue({
            camper: (
              <span className={isActive ? styles.chevronActive : ''}>
                <ChevronSVG />
              </span>
            ),
            camperlab: (
              <span className={isActive ? styles.chevronActive : ''}>
                <ChevronSVG />
              </span>
            ),
            nnormal: isActive ? <MinusOutlined /> : <PlusOutlined />,
          });
        }}
        expandIconPosition="right"
        className={styles.customAccordion}
        onChange={handlerDataOpenAccordion}
      >
        {productSheet.productDescription !== null ?
          //  @ts-ignore Antd!!
          <Panel
            className={styles.descriptionBlock}
            header={<h2 className={styles.accordionHeader}>{t('ficha.producto', 'description', 'Description')}</h2>}
            key="1"
          >
            <span
              className={styles.content}
              dangerouslySetInnerHTML={{
                __html: productSheet.productDescription?.text,
              }}
            />
            {getDomainValue({
              camper: null,
              camperlab: null,
              nnormal: <p className={styles.sku}>SKU: {productSheet.code}</p>,
            })}
            {productSheet.productDescription.hasModal && (
              <Tooltip
                placement="top"
                title={<span dangerouslySetInnerHTML={{ __html: productSheet.productDescription.modalText }}></span>}
                overlayClassName={styles.tooltip}
                arrowPointAtCenter
                trigger="click"
                // @ts-ignore Antd!!
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                onVisibleChange={(visible) => {
                  setOpenDescriptionModal(visible);
                }}
              >
                {openDescriptionModal ?
                  <MinusCircleOutlined />
                : <PlusCircleOutlined />}
              </Tooltip>
            )}
          </Panel>
        : null}
        {productSheet.features && !productSheet.recycled && (
          // @ts-ignore Antd!!
          <Panel header={<h2 className={styles.accordionHeader}>{t('ficha.producto', 'caracteristicas', 'Features')}</h2>} key="2">
            {productSheet.features.map((featureItem, index) => {
              const { hasModal, value } = featureItem;
              const valueHtml = value === undefined ? '' : value;
              let featureContent = (
                <div key={`feature-${index}`} className={styles.contentFeatures}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: `${featureItem.name}${valueHtml}`,
                    }}
                  />
                </div>
              );

              if (hasModal) {
                featureContent = (
                  <div key={`feature-${index}`} className={styles.contentFeatures}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `${featureItem.name}${valueHtml}`,
                      }}
                    />
                    <Tooltip
                      placement="top"
                      title={<span dangerouslySetInnerHTML={{ __html: featureItem.modalText }}></span>}
                      overlayClassName={styles.tooltip}
                      arrowPointAtCenter
                      trigger="click"
                      // @ts-ignore Antd!!
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      onVisibleChange={(visible) => {
                        setOpenTooltipIndex(visible ? index : null);
                      }}
                    >
                      {openTooltipIndex === index ?
                        <MinusCircleOutlined />
                      : <PlusCircleOutlined />}
                    </Tooltip>
                  </div>
                );
              }

              return featureContent;
            })}
          </Panel>
        )}
        {productSheet.care && !productSheet.recycled && (
          // @ts-ignore Antd!!
          <Panel header={<h2 className={styles.accordionHeader}>{t('ficha.producto', 'shoe.care', 'Product Care')}</h2>} key="3">
            {productSheet.careTextArray && productSheet.careTextArray.length > 0 && productSheet.careTextArray.some((text) => !isBlank(text)) && (
              <div
                className={styles.contentFeatures}
                dangerouslySetInnerHTML={{
                  __html: productSheet.careTextArray[0],
                }}
              />
            )}
            <br />
            {productSheet.careDetailsArray &&
              productSheet.careDetailsArray.length > 0 &&
              productSheet.careDetailsArray.map((careDetail, index) => (
                <div
                  key={index}
                  className={styles.contentFeatures}
                  dangerouslySetInnerHTML={{
                    __html: careDetail,
                  }}
                />
              ))}
            {productSheet.careTextArray && productSheet.careTextArray.length > 1 && productSheet.careTextArray.some((text) => !isBlank(text)) && (
              <div
                className={styles.contentFeatures}
                dangerouslySetInnerHTML={{
                  __html: productSheet.careTextArray[1],
                }}
              />
            )}
          </Panel>
        )}
        {productSheet?.productSheetInfo?.map((section, index) => (
          // @ts-ignore Antd!!
          <Panel key={`product-sheet-info-${section.title}-${index}`} header={<h2 className={styles.accordionHeader}>{section.title}</h2>}>
            <div
              className={styles.contentFeatures}
              dangerouslySetInnerHTML={{
                __html: section.content,
              }}
            />
          </Panel>
        ))}
        {Array.isArray(productSheet.productMeasures) && productSheet.productMeasures.length > 0 ?
          // @ts-ignore Antd!!
          <Collapse.Panel key="product-sheet-info-parts-sizing" header={<h2 className="">{productSheet.productPartsSizing}</h2>}>
            <div className="flex flex-col gap-2">
              {productSheet.productMeasures.map((measure) => (
                <div key={`product-measure-${measure.img}`} className="grid grid-cols-[60px_1fr] gap-3">
                  <picture className="block h-full w-full">
                    <source srcSet={`${measureImgSrc}/${measure.img}.webp`} type="image/webp" />
                    <img height={60} width={60} src={`${measureImgSrc}/${measure.img}.png`} alt="" />
                  </picture>
                  <div className="flex flex-col justify-center gap-0.5 font-primary text-sm">
                    <p className="mb-0 font-moderne font-medium">{measure.titulo}</p>
                    <p className="mb-0">{measure.medida}</p>
                  </div>
                </div>
              ))}
            </div>
          </Collapse.Panel>
        : null}
        {!isBlank(productSheet?.productProcessingText) ?
          // @ts-ignore Antd!!
          <Panel
            key="product-sheet-processing-text"
            header={<h2 className={styles.accordionHeader} dangerouslySetInnerHTML={{ __html: productSheet?.productProcessing }} />}
          >
            <div
              className={styles.contentFeatures}
              dangerouslySetInnerHTML={{
                __html: productSheet?.productProcessingText,
              }}
            />
          </Panel>
        : null}
      </Collapse>
      {getDomainValue({
        camper: null,
        nnormal: (
          <ProductShare
            baseUrl={productSheet.baseUrl}
            code={productSheet.code}
            image={productSheet.images && productSheet.images[0] ? productSheet.images[0].lg : ''}
          />
        ),
        camperlab: null,
      })}
    </div>
  );
}
